import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, showAlertObservable, showDialogObservable} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Api from './api';
import * as Actions from './actions';
import Session from './model';
import * as Constants from './constants';

export const fetchObservable = (sessionId, includePricesForAllPricingGroups) => {
    return Api.fetchSession(sessionId, includePricesForAllPricingGroups).map(newSessionsData =>
        Actions.fetchSuccess.action(newSessionsData.sessions),
    );
};

export const fetchAllObservable = (
    institutionId,
    validOn,
    validTo,
    ageGroupId,
    includeDiscontinued,
    filterCurrent = true,
    includePricesForAllPricingGroups,
) => {
    return Api.fetchSessionsObservable(
        institutionId,
        validOn,
        validTo,
        ageGroupId,
        includeDiscontinued,
        includePricesForAllPricingGroups,
    ).map(newSessionsData =>
        Actions.fetchAllSuccess.action(newSessionsData.sessions, newSessionsData.behaviors, filterCurrent),
    );
};

export const createSessionObservable = session => {
    return Api.createSession(Session.toAPI(session)).map(sessionData =>
        Actions.createSuccess.action(sessionData.session, sessionData.session.institutionId),
    );
};

export const updateSessionObservable = (session, force = false) => {
    return Api.updateSession(Session.toAPI(session), force).map(sessionData =>
        Actions.updateSuccess.action(session, sessionData.session, sessionData.session.institutionId),
    );
};

export const removeSessionObservable = session => {
    return Api.deleteSession(Session.toAPI(session)).map(sessionData =>
        Actions.removeSuccess.action(sessionData.session, sessionData.session.institutionId),
    );
};

export const restoreSessionObservable = session => {
    return Api.restoreSession(Session.toAPI(session)).map(sessionData =>
        Actions.restoreSuccess.action(sessionData.session, sessionData.session.institutionId),
    );
};

const unscheduleSessionObservable = (sessionId, last) => {
    return Api.unscheduleSession(sessionId).map(sessionData =>
        Actions.unscheduleSuccess.action(sessionData.unscheduledSessionVersionId, last, sessionData.remainingVersions),
    );
};

const fetchSession = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}) =>
            fetchObservable(payload.sessionId, payload.includePricesForAllPricingGroups).catch(e =>
                Observable.of(Actions.fetchFailed.action(e)),
            ),
        );

const fetchAll = action$ =>
    action$
        .ofType(Actions.fetchAll.type)
        .mergeMap(({payload}) =>
            fetchAllObservable(
                payload.institutionId,
                payload.validOn,
                payload.validTo,
                payload.ageGroupId,
                payload.includeDiscontinued,
                payload.filterCurrent,
                payload.includePricesForAllPricingGroups,
            ).catch(e => Observable.of(Actions.fetchAllFailed.action(e))),
        );

const createSession = action$ =>
    action$.ofType(Actions.create.type).mergeMap(({payload}) =>
        createSessionObservable(payload.session)
            .flatMap(action => [GlobalEventActions.updateSuccess.action(i18next.t('session.sessionCreated')), action])
            .catch(e => Observable.of(Actions.createFailed.action(e))),
    );

const getSessionBookedDescription = session => {
    if (session.validTo) {
        return i18next.t('session.sessionBookedDescriptionValidTo', {
            sessionTitle: session.title,
            validFrom: session.validFrom.format('L'),
            validTo: session.validFrom.format('L'),
        });
    }
    return i18next.t('session.sessionBookedDescription', {
        sessionTitle: session.title,
        validFrom: session.validFrom.format('L'),
    });
};

const getPackageBookedDescription = session => {
    if (session.validTo) {
        return i18next.t('session.packageBookedDescriptionValidTo', {
            packageTitle: session.title,
            validFrom: session.validFrom.format('L'),
            validTo: session.validFrom.format('L'),
        });
    }
    return i18next.t('session.packageBookedDescription', {
        packageTitle: session.title,
        validFrom: session.validFrom.format('L'),
    });
};

const updateSession = action$ =>
    action$.ofType(Actions.update.type).mergeMap(({payload}) =>
        updateSessionObservable(payload.session)
            .flatMap(action => [
                GlobalEventActions.updateSuccess.action(
                    i18next.t(payload.newVersion ? 'session.sessionChangesSaved' : 'session.sessionUpdated'),
                ),
                action,
            ])
            .catch(e => {
                if (e.errorCode === Constants.RequiresForceUpdateErrorCode) {
                    const title =
                        payload.session.sessionType === Session.SessionType.STANDALONE_SESSION ||
                        payload.session.sessionType === Session.SessionType.VOLUME_SESSION
                            ? i18next.t('session.sessionBooked')
                            : i18next.t('session.packageBooked');

                    const description =
                        payload.session.sessionType === Session.SessionType.STANDALONE_SESSION ||
                        payload.session.sessionType === Session.SessionType.VOLUME_SESSION
                            ? getSessionBookedDescription(payload.session)
                            : getPackageBookedDescription(payload.session);

                    return showDialogObservable({title, description}).mergeMap(selection => {
                        if (selection === 'OK') {
                            return updateSessionObservable(payload.session, true)
                                .flatMap(action => [
                                    GlobalEventActions.updateSuccess.action(
                                        i18next.t(
                                            payload.newVersion
                                                ? 'session.sessionChangesSaved'
                                                : 'session.sessionUpdated',
                                        ),
                                    ),
                                    action,
                                ])
                                .catch(() => Observable.of(Actions.updateFailed.action(payload.session)));
                        } else {
                            return Observable.of(Actions.updateAborted.action(payload.session));
                        }
                    });
                }
                return Observable.of(Actions.updateFailed.action(payload.session));
            }),
    );

const removeSession = action$ =>
    action$.ofType(Actions.remove.type).mergeMap(({payload}) => {
        const description =
            payload.session.sessionType === Session.SessionType.STANDALONE_SESSION ||
            payload.session.sessionType === Session.SessionType.VOLUME_SESSION
                ? i18next.t('session.sessionDeleteDescription', {sessionTitle: payload.session.title})
                : i18next.t('session.packageDeleteDescription', {packageTitle: payload.session.title});

        return showDialogObservable({title: i18next.t('session.confirmDelete'), description}).mergeMap(selection => {
            if (selection === 'OK') {
                return removeSessionObservable(payload.session)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(i18next.t('session.sessionDeleted')),
                        action,
                    ])
                    .catch(e => {
                        if (e.errorCode === 1476097841) {
                            const errorMessage =
                                payload.session.sessionType === Session.SessionType.STANDALONE_SESSION ||
                                payload.session.sessionType === Session.SessionType.VOLUME_SESSION
                                    ? i18next.t('session.sessionDeleteBookedError')
                                    : i18next.t('session.packageDeleteBookedError');

                            return showAlertObservable({title: '', description: errorMessage}).mergeMap(() =>
                                Observable.of(Actions.removeFailed.action(payload.session)),
                            );
                        }

                        return Observable.of(Actions.removeFailed.action(payload.session));
                    });
            } else {
                return Observable.of(Actions.removeAborted.action(payload.session));
            }
        });
    });

const restoreSession = action$ =>
    action$.ofType(Actions.restore.type).mergeMap(({payload}) => {
        const description =
            payload.session.sessionType === Session.SessionType.STANDALONE_SESSION ||
            payload.session.sessionType === Session.SessionType.VOLUME_SESSION
                ? i18next.t('session.sessionRestoreDescription', {sessionTitle: payload.session.title})
                : i18next.t('session.packageRestoreDescription', {packageTitle: payload.session.title});

        return showDialogObservable({title: i18next.t('areYouSure'), description}).mergeMap(selection => {
            if (selection === 'OK') {
                return restoreSessionObservable(payload.session)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(i18next.t('session.sessionRestored')),
                        action,
                    ])
                    .catch(e => Observable.of(Actions.restoreFailed.action(e)));
            }
            return Observable.of(Actions.restoreAborted.action());
        });
    });

const unscheduleSession = action$ =>
    action$.ofType(Actions.unschedule.type).mergeMap(({payload}) => {
        return showDeleteDialogObservable({
            title: i18next.t('areYouSure'),
            description: i18next.t(
                payload.last
                    ? 'plans.manageProduct.deleteDescription'
                    : 'plans.manageProduct.unscheduleChangeDescription',
                {
                    title: payload.session.title,
                    date: payload.session.validFrom.format('ll'),
                },
            ),
            okText: i18next.t('confirm'),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return unscheduleSessionObservable(payload.session.id, payload.last)
                    .flatMap(action => [
                        GlobalEventActions.updateSuccess.action(
                            i18next.t(
                                payload.last
                                    ? 'plans.manageProduct.deleteSuccessful'
                                    : 'plans.manageProduct.unscheduleSuccessful',
                                {
                                    title: payload.session.title,
                                },
                            ),
                        ),
                        action,
                    ])
                    .catch(e => Observable.of(Actions.unscheduleFailed.action(e)));
            } else {
                return Observable.of(Actions.unscheduleAborted.action(payload.session));
            }
        });
    });

export default combineEpics(
    fetchSession,
    fetchAll,
    createSession,
    updateSession,
    restoreSession,
    removeSession,
    unscheduleSession,
);
